/**
 * TierPricingTab — Editor prezzi tier per super_admin.
 *
 * Per ogni tier (free/pro/business) mostra una card con:
 *  - label (display name)
 *  - tagline (claim breve)
 *  - prezzo mensile / annuale per seat
 *  - trial_days
 *  - Stripe price IDs (read-only — sync via script setup-stripe-products.cjs)
 *
 * Salva live a Firestore. La modale upsell legge questi prezzi senza
 * redeploy.
 *
 * @since 2026-05-18
 */

import React, { useState } from 'react';
import {
  Box, Card, Stack, Typography, TextField, Button, Chip, Alert,
  CircularProgress, alpha,
} from '@mui/material';
import { Save, Check } from '@mui/icons-material';
import { tokens } from '../../theme';
import type { TierDefDoc } from '../../routes/PricingPage';

const TIER_COLOR_MAP: Record<string, string> = {
  base: tokens.color.tierFree.main,
  free: tokens.color.tierFree.main, // alias legacy
  pro: tokens.color.tierPro.main,
  business: tokens.color.tierBusiness.main,
};

interface TierPricingTabProps {
  tiers: TierDefDoc[];
  onUpdate: (tierId: string, patch: Partial<TierDefDoc>) => Promise<void>;
}

export const TierPricingTab: React.FC<TierPricingTabProps> = ({ tiers, onUpdate }) => {
  return (
    <Stack spacing={2.5}>
      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
        I prezzi vengono letti live da Firestore dalla modale upsell e dalla pagina <code>/billing/upgrade</code> dell'app utente.
        Per sincronizzare gli Stripe Product/Price ID dopo aver cambiato un prezzo, esegui:
        <Box component="code" sx={{ display: 'block', mt: 1, fontFamily: tokens.font.mono, fontSize: '0.78rem' }}>
          STRIPE_SECRET_KEY=&lt;sk_xxx&gt; node scripts/setup-stripe-products.cjs
        </Box>
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        {tiers.map((tier) => (
          <TierCard key={tier.id} tier={tier} onUpdate={onUpdate} />
        ))}
      </Stack>
    </Stack>
  );
};

interface TierCardProps {
  tier: TierDefDoc;
  onUpdate: (tierId: string, patch: Partial<TierDefDoc>) => Promise<void>;
}

const TierCard: React.FC<TierCardProps> = ({ tier, onUpdate }) => {
  const [label, setLabel] = useState(tier.label ?? '');
  const [tagline, setTagline] = useState(tier.tagline ?? '');
  const [priceMonthly, setPriceMonthly] = useState(String(tier.price_per_seat_eur_monthly ?? 0));
  const [priceYearly, setPriceYearly] = useState(String(tier.price_per_seat_eur_yearly ?? 0));
  const [trial, setTrial] = useState(String(tier.trial_days ?? 0));
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const color = TIER_COLOR_MAP[tier.id] ?? tokens.color.graphite;

  const dirty =
    label !== (tier.label ?? '') ||
    tagline !== (tier.tagline ?? '') ||
    Number(priceMonthly) !== (tier.price_per_seat_eur_monthly ?? 0) ||
    Number(priceYearly) !== (tier.price_per_seat_eur_yearly ?? 0) ||
    Number(trial) !== (tier.trial_days ?? 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(tier.id, {
        label: label.trim(),
        tagline: tagline.trim(),
        price_per_seat_eur_monthly: Number(priceMonthly),
        price_per_seat_eur_yearly: Number(priceYearly),
        trial_days: Number(trial),
      });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  // Auto-calcolo sconto annuale rispetto al monthly × 12
  const monthlyTotal = Number(priceMonthly) * 12;
  const yearlyTotal = Number(priceYearly);
  const discountPct = monthlyTotal > 0 ? Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100) : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        p: 2.5,
        borderColor: alpha(color, 0.25),
        borderRadius: 3,
        background: `linear-gradient(180deg, ${alpha(color, 0.025)} 0%, ${tokens.color.paper} 80%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />

      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Chip
            label={tier.id.toUpperCase()}
            size="small"
            sx={{
              bgcolor: color,
              color: '#fff',
              fontWeight: 700,
              fontFamily: tokens.font.mono,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
            }}
          />
          {tier.stripe_product_id && (
            <Typography
              sx={{ fontFamily: tokens.font.mono, fontSize: '0.65rem', color: tokens.color.mist }}
              title={tier.stripe_product_id}
            >
              {tier.stripe_product_id.slice(0, 14)}…
            </Typography>
          )}
        </Stack>

        <TextField
          label="Display name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Tagline"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={3}
        />

        <Stack direction="row" spacing={1}>
          <TextField
            label="€ / seat / mese"
            value={priceMonthly}
            onChange={(e) => setPriceMonthly(e.target.value.replace(/[^\d.]/g, ''))}
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 1 }}
          />
          <TextField
            label="€ / seat / anno"
            value={priceYearly}
            onChange={(e) => setPriceYearly(e.target.value.replace(/[^\d.]/g, ''))}
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 1 }}
          />
        </Stack>
        {discountPct > 0 && (
          <Typography variant="caption" sx={{ color: tokens.color.success, fontWeight: 600 }}>
            Sconto annuale: -{discountPct}% (€{(monthlyTotal - yearlyTotal).toFixed(0)} risparmiati)
          </Typography>
        )}

        <TextField
          label="Trial days"
          value={trial}
          onChange={(e) => setTrial(e.target.value.replace(/\D/g, ''))}
          size="small"
          fullWidth
          type="number"
          inputProps={{ min: 0, step: 1 }}
          helperText="Giorni di prova gratuita all'attivazione subscription"
        />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={savedTick ? <Check /> : <Save />}
            onClick={handleSave}
            disabled={!dirty || saving}
            sx={{
              bgcolor: color,
              '&:hover': { bgcolor: color, filter: 'brightness(0.92)' },
            }}
          >
            {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : savedTick ? 'Salvato' : 'Salva'}
          </Button>
          {dirty && !saving && (
            <Typography variant="caption" sx={{ color: tokens.color.warning, fontWeight: 600 }}>
              Modifiche non salvate
            </Typography>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
